import { zodResolver } from '@hookform/resolvers/zod';
import {
  type UitSignerEmailResponse,
  UitSignerEmailResponse as UitSignerEmailResponseSchema,
  type UitSignerEmailUpdateInput,
  UitSignerEmailUpdateInput as UitSignerEmailUpdateInputSchema,
} from '@uit/shared/schemas/settings';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { apiMutation, useApi } from '../../lib/api.js';
import s from '../../pages/SettingsPage.module.scss';

export function UitSignerEmailSection() {
  const { data, refetch } = useApi<UitSignerEmailResponse>(
    '/api/settings/uit-signer-email',
    UitSignerEmailResponseSchema,
  );
  const [success, setSuccess] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UitSignerEmailUpdateInput>({
    resolver: zodResolver(UitSignerEmailUpdateInputSchema),
    defaultValues: { uit_signer_email: '' },
  });

  // Seed the field once the current value loads.
  useEffect(() => {
    if (data) reset({ uit_signer_email: data.uit_signer_email });
  }, [data, reset]);

  const onSubmit = async (values: UitSignerEmailUpdateInput): Promise<void> => {
    setSubmitting(true);
    setSubmitError(null);
    setSuccess(null);
    try {
      await apiMutation<UitSignerEmailUpdateInput, UitSignerEmailResponse>(
        '/api/settings/uit-signer-email',
        values,
        { method: 'PATCH', schema: UitSignerEmailResponseSchema },
      );
      setSuccess('Saved.');
      void refetch();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setSubmitError(message.includes('403') ? 'You do not have permission.' : 'Could not save.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>uIT signer email</h2>
      <p className={s.sectionSubtitle}>
        The single uIT email used as the contract countersigner and the “contractor signed”
        notification, for every onboarding.
      </p>

      <form
        onSubmit={(e) => {
          void handleSubmit(onSubmit)(e);
        }}
        noValidate
      >
        <div className={s.field}>
          <label className={s.label} htmlFor="uit-signer-email">
            Signer email
          </label>
          <input
            id="uit-signer-email"
            type="email"
            autoComplete="off"
            className={s.input}
            aria-invalid={!!errors.uit_signer_email}
            {...register('uit_signer_email')}
          />
          {errors.uit_signer_email && (
            <div className={s.fieldError}>{errors.uit_signer_email.message}</div>
          )}
        </div>

        <button type="submit" className={s.button} disabled={submitting}>
          {submitting ? 'Saving…' : 'Save'}
        </button>

        {success && (
          <div className={s.success} role="status">
            {success}
          </div>
        )}
        {submitError && (
          <div className={s.error} role="alert">
            {submitError}
          </div>
        )}
      </form>
    </section>
  );
}
